import React, { useEffect, useRef } from 'react';
import { Home, Users, ShoppingBag, BarChart2, Settings, Bell, DollarSign, ShoppingCart, TrendingUp } from 'lucide-react';
import type { DesignSystem } from './DesignSystemPanel.type';

const FONT_FAMILIES: Record<string, string> = {
  'System Default': "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  'Inter': "'Inter', -apple-system, sans-serif",
  'Poppins': "'Poppins', sans-serif",
  'Montserrat': "'Montserrat', sans-serif",
  'Roboto': "'Roboto', sans-serif",
  'Open Sans': "'Open Sans', sans-serif"
};

const getFontCssFromName = (fontName: string): string => {
  return FONT_FAMILIES[fontName] || FONT_FAMILIES['System Default'];
};

interface DesignSystemPreviewProps {
  designSystem: DesignSystem | null;
}

const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const DesignSystemPreview: React.FC<DesignSystemPreviewProps> = ({ designSystem }) => {
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fontStylesheetId = 'design-system-google-fonts';
    
    if (document.getElementById(fontStylesheetId)) {
      return;
    }
    
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);

    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);

    const fontLink = document.createElement('link');
    fontLink.id = fontStylesheetId;
    fontLink.rel = 'stylesheet';
    fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&family=Montserrat:wght@400;500;600;700&family=Roboto:wght@400;500;700&family=Open+Sans:wght@400;500;600;700&display=swap';
    
    fontLink.onload = () => {
      const fontPreloader = document.createElement('div');
      fontPreloader.style.position = 'absolute';
      fontPreloader.style.visibility = 'hidden';
      fontPreloader.style.pointerEvents = 'none';
      fontPreloader.innerHTML = `
        <span style="font-family: 'Inter';">.</span>
        <span style="font-family: 'Poppins';">.</span>
        <span style="font-family: 'Montserrat';">.</span>
        <span style="font-family: 'Roboto';">.</span>
        <span style="font-family: 'Open Sans';">.</span>
      `;
      document.body.appendChild(fontPreloader);
      
      setTimeout(() => {
        if (fontPreloader.parentNode) {
          document.body.removeChild(fontPreloader);
        }
      }, 1000);
    };
    
    document.head.appendChild(fontLink);
  }, []);

  useEffect(() => {
    if (!designSystem || !previewRef.current) return;

    const preview = previewRef.current;
    const { colors, typography, spacing, borders } = designSystem;

    preview.style.setProperty('--preview-bg-color', colors.background);
    preview.style.setProperty('--preview-text-color', colors.textColor);
    preview.style.setProperty('--preview-muted-bg', colors.mutedBg);
    preview.style.setProperty('--preview-muted-text', colors.mutedText);
    preview.style.setProperty('--preview-primary-bg', colors.primaryBg);
    preview.style.setProperty('--preview-primary-text', colors.primaryText);
    preview.style.setProperty('--preview-secondary-bg', colors.secondaryBg);
    preview.style.setProperty('--preview-secondary-text', colors.secondaryText);
    preview.style.setProperty('--preview-accent-bg', colors.accentBg);
    preview.style.setProperty('--preview-accent-text', colors.accentText);
    preview.style.setProperty('--preview-destructive-bg', colors.destructiveBg);
    preview.style.setProperty('--preview-destructive-text', colors.destructiveText);
    preview.style.setProperty('--preview-input-bg', colors.input);
    preview.style.setProperty('--preview-border-color', colors.border);
    preview.style.setProperty('--preview-focus-border', colors.focusBorder);
    preview.style.setProperty('--preview-border-radius', `${borders.radiusRem}rem`);
    preview.style.setProperty('--preview-surface-color', colors.background);

    const styleId = 'design-system-preview-font-override';
    let styleTag = document.getElementById(styleId) as HTMLStyleElement;
    
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }
    
    const fontFamilyCss = getFontCssFromName(typography.fontFamily);
    
    styleTag.textContent = `
      #design-system-preview-container,
      #design-system-preview-container * {
        font-family: ${fontFamilyCss} !important;
      }
    `;

    const sizes = typography.fontSizeValues || { base: 12, medium: 14, large: 18, xlarge: 20 };
    
    preview.style.setProperty('--preview-font-size-base', `${sizes.base}px`);
    preview.style.setProperty('--preview-font-size-medium', `${sizes.medium}px`);
    preview.style.setProperty('--preview-font-size-large', `${sizes.large}px`);
    preview.style.setProperty('--preview-font-size-xlarge', `${sizes.xlarge}px`);

    const spacingConfig = spacing.spacingValues || { padding: 16, gap: 16, margin: 24 };
    
    preview.style.setProperty('--preview-spacing-padding', `${spacingConfig.padding}px`);
    preview.style.setProperty('--preview-spacing-gap', `${spacingConfig.gap}px`);
    preview.style.setProperty('--preview-spacing-margin', `${spacingConfig.margin}px`);

  }, [designSystem]);

  if (!designSystem) {
    return (
      <div className="h-full flex items-center justify-center bg-bg-secondary">
        <p className="text-text-secondary text-text-sm">Select a theme to see preview</p>
      </div>
    );
  }

  return (
    <div 
      id="design-system-preview-container"
      ref={previewRef}
      className="h-full overflow-auto modern-scrollbar relative"
      style={{ 
        background: 'var(--preview-bg-color)',
        color: 'var(--preview-text-color)'
      }}
    >
      <div style={{ 
        minWidth: '37.5rem',
        padding: 'var(--preview-spacing-margin)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--preview-spacing-gap)'
      }}>
        {/* Dashboard Layout */}
        <div className="flex" style={{ gap: 'var(--preview-spacing-gap)' }}>
          {/* Sidebar */}
          <div 
            className="flex-shrink-0 rounded overflow-hidden"
            style={{ 
              width: '12rem',
              background: 'var(--preview-primary-bg)',
              borderRadius: 'var(--preview-border-radius)'
            }}
          >
            <div style={{ 
              padding: 'var(--preview-spacing-padding)', 
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)' 
            }}>
              <div className="flex items-center" style={{ gap: 'var(--preview-spacing-gap)' }}>
                <div 
                  className="rounded flex items-center justify-center"
                  style={{ 
                    width: '2rem',
                    height: '2rem',
                    background: 'rgba(255, 255, 255, 0.2)' 
                  }}
                >
                  <Home style={{ width: '1rem', height: '1rem', color: 'var(--preview-primary-text)' }} />
                </div>
                <span className="font-semibold" style={{ 
                  color: 'var(--preview-primary-text)',
                  fontSize: 'var(--preview-font-size-medium)'
                }}>
                  MyApp
                </span>
              </div>
            </div>
            <nav style={{ padding: 'calc(var(--preview-spacing-padding) / 2)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'calc(var(--preview-spacing-gap) / 3)' }}>
                {[
                  { icon: Home, label: 'Dashboard', active: true },
                  { icon: Users, label: 'Customers' },
                  { icon: ShoppingBag, label: 'Orders' },
                  { icon: BarChart2, label: 'Analytics' },
                  { icon: Settings, label: 'Settings' }
                ].map(({ icon: Icon, label, active }) => (
                  <button
                    key={label}
                    className="w-full flex items-center font-medium transition-colors"
                    style={{
                      gap: 'calc(var(--preview-spacing-gap) / 2)',
                      padding: 'calc(var(--preview-spacing-padding) / 1.5)',
                      background: active ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                      color: active ? 'var(--preview-primary-text)' : 'rgba(255, 255, 255, 0.7)',
                      borderRadius: 'calc(var(--preview-border-radius) * 0.8)',
                      fontSize: 'var(--preview-font-size-base)',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <Icon style={{ width: '1rem', height: '1rem' }} />
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--preview-spacing-gap)' }}>
            {/* Top Bar */}
            <div 
              className="rounded"
              style={{ 
                padding: 'var(--preview-spacing-padding)',
                background: 'var(--preview-surface-color)',
                border: '1px solid var(--preview-border-color)',
                borderRadius: 'var(--preview-border-radius)'
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="font-semibold" style={{ 
                    color: 'var(--preview-text-color)',
                    fontSize: 'var(--preview-font-size-large)',
                    marginBottom: 'calc(var(--preview-spacing-gap) / 4)'
                  }}>
                    Welcome back!
                  </h1>
                  <p style={{ 
                    color: 'var(--preview-muted-text)',
                    fontSize: 'var(--preview-font-size-base)'
                  }}>
                    Here's what's happening today
                  </p>
                </div>
                <div className="flex items-center" style={{ gap: 'calc(var(--preview-spacing-gap) / 2)' }}>
                  <button 
                    className="rounded relative"
                    style={{ 
                      padding: 'calc(var(--preview-spacing-padding) / 2)',
                      background: 'var(--preview-muted-bg)',
                      borderRadius: 'calc(var(--preview-border-radius) * 0.8)',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    <Bell style={{ width: '1rem', height: '1rem', color: 'var(--preview-text-color)' }} />
                    <span 
                      className="absolute rounded-full"
                      style={{ 
                        top: '0.375rem',
                        right: '0.375rem',
                        width: '0.375rem',
                        height: '0.375rem',
                        background: 'var(--preview-destructive-bg)' 
                      }}
                    ></span>
                  </button>
                  <div 
                    className="rounded-full"
                    style={{ 
                      width: '2rem',
                      height: '2rem',
                      background: 'var(--preview-primary-bg)' 
                    }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-4" style={{ gap: 'var(--preview-spacing-gap)' }}>
              {[
                { icon: DollarSign, label: 'Revenue', value: '$45,231', change: '+12%', color: '#10B981' },
                { icon: Users, label: 'Customers', value: '2,543', change: '+8%', color: 'var(--preview-primary-bg)' },
                { icon: ShoppingCart, label: 'Orders', value: '1,234', change: '+5%', color: 'var(--preview-accent-bg)' },
                { icon: TrendingUp, label: 'Active', value: '573', change: '+2%', color: '#F59E0B' }
              ].map(({ icon: Icon, label, value, change, color }) => (
                <div 
                  key={label}
                  className="rounded"
                  style={{ 
                    padding: 'var(--preview-spacing-padding)',
                    background: 'var(--preview-surface-color)',
                    border: '1px solid var(--preview-border-color)',
                    borderRadius: 'var(--preview-border-radius)'
                  }}
                >
                  <div className="flex items-center justify-between" style={{ marginBottom: 'calc(var(--preview-spacing-gap) / 2)' }}>
                    <div 
                      className="rounded flex items-center justify-center"
                      style={{ 
                        width: '2.25rem',
                        height: '2.25rem',
                        background: hexToRgba(color, 0.1),
                        borderRadius: 'calc(var(--preview-border-radius) * 0.8)'
                      }}
                    >
                      <Icon style={{ width: '1rem', height: '1rem', color }} />
                    </div>
                    <span 
                      className="font-semibold rounded"
                      style={{ 
                        fontSize: 'var(--preview-font-size-base)',
                        padding: 'calc(var(--preview-spacing-padding) / 3) calc(var(--preview-spacing-padding) / 2)',
                        background: hexToRgba('#10B981', 0.15),
                        color: '#10B981',
                        borderRadius: 'calc(var(--preview-border-radius) * 0.6)'
                      }}
                    >
                      {change}
                    </span>
                  </div>
                  <p style={{ 
                    color: 'var(--preview-muted-text)',
                    fontSize: 'var(--preview-font-size-base)',
                    marginBottom: 'calc(var(--preview-spacing-gap) / 3)'
                  }}>
                    {label}
                  </p>
                  <p className="font-semibold" style={{ 
                    color: 'var(--preview-text-color)',
                    fontSize: 'var(--preview-font-size-xlarge)'
                  }}>
                    {value}
                  </p>
                </div>
              ))}
            </div>

            {/* Chart and Form */}
            <div className="grid grid-cols-2" style={{ gap: 'var(--preview-spacing-gap)' }}>
              {/* Chart Card */}
              <div 
                className="rounded overflow-hidden"
                style={{ 
                  background: 'var(--preview-surface-color)',
                  border: '1px solid var(--preview-border-color)',
                  borderRadius: 'var(--preview-border-radius)'
                }}
              >
                <div style={{ 
                  padding: 'var(--preview-spacing-padding)', 
                  borderBottom: '1px solid var(--preview-border-color)' 
                }}>
                  <h3 className="font-semibold" style={{ 
                    color: 'var(--preview-text-color)',
                    fontSize: 'var(--preview-font-size-medium)'
                  }}>
                    Sales Overview
                  </h3>
                </div>
                <div style={{ 
                  padding: 'var(--preview-spacing-padding)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--preview-spacing-gap)'
                }}>
                  {['65%', '78%', '92%'].map((width, idx) => (
                    <div key={idx} className="flex items-center" style={{ gap: 'calc(var(--preview-spacing-gap) / 2)' }}>
                      <span style={{ 
                        width: '1.5rem',
                        color: 'var(--preview-muted-text)',
                        fontSize: 'var(--preview-font-size-base)'
                      }}>
                        {['Jan', 'Feb', 'Mar'][idx]}
                      </span>
                      <div 
                        className="flex-1 rounded overflow-hidden"
                        style={{ 
                          height: '1.75rem',
                          background: 'var(--preview-muted-bg)',
                          borderRadius: 'calc(var(--preview-border-radius) * 0.8)'
                        }}
                      >
                        <div 
                          className="h-full rounded transition-all"
                          style={{ 
                            width,
                            background: 'var(--preview-primary-bg)',
                            borderRadius: 'calc(var(--preview-border-radius) * 0.8)'
                          }}
                        ></div>
                      </div>
                      <span className="text-right font-semibold" style={{ 
                        width: '2rem',
                        color: 'var(--preview-text-color)',
                        fontSize: 'var(--preview-font-size-base)'
                      }}>
                        {width}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Form Card */}
              <div 
                className="rounded overflow-hidden"
                style={{ 
                  background: 'var(--preview-surface-color)',
                  border: '1px solid var(--preview-border-color)',
                  borderRadius: 'var(--preview-border-radius)'
                }}
              >
                <div style={{ 
                  padding: 'var(--preview-spacing-padding)', 
                  borderBottom: '1px solid var(--preview-border-color)' 
                }}>
                  <h3 className="font-semibold" style={{ 
                    color: 'var(--preview-text-color)',
                    fontSize: 'var(--preview-font-size-medium)'
                  }}>
                    Add Customer
                  </h3>
                </div>
                <div style={{ 
                  padding: 'var(--preview-spacing-padding)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--preview-spacing-gap)'
                }}>
                  <div>
                    <label className="block font-medium" style={{ 
                      color: 'var(--preview-text-color)',
                      fontSize: 'var(--preview-font-size-base)',
                      marginBottom: 'calc(var(--preview-spacing-gap) / 3)'
                    }}>
                      Full Name
                    </label>
                    <input
                      type="text"
                      placeholder="John Smith"
                      className="w-full rounded transition-all"
                      style={{ 
                        padding: 'calc(var(--preview-spacing-padding) / 1.5)',
                        background: 'var(--preview-input-bg)',
                        border: '2px solid var(--preview-border-color)',
                        color: 'var(--preview-text-color)',
                        borderRadius: 'calc(var(--preview-border-radius) * 0.8)',
                        fontSize: 'var(--preview-font-size-base)'
                      }}
                    />
                  </div>
                  <div>
                    <label className="block font-medium" style={{ 
                      color: 'var(--preview-text-color)',
                      fontSize: 'var(--preview-font-size-base)',
                      marginBottom: 'calc(var(--preview-spacing-gap) / 3)'
                    }}>
                      Email
                    </label>
                    <input
                      type="email"
                      placeholder="john@company.com"
                      className="w-full rounded transition-all"
                      style={{ 
                        padding: 'calc(var(--preview-spacing-padding) / 1.5)',
                        background: 'var(--preview-input-bg)',
                        border: '2px solid var(--preview-border-color)',
                        color: 'var(--preview-text-color)',
                        borderRadius: 'calc(var(--preview-border-radius) * 0.8)',
                        fontSize: 'var(--preview-font-size-base)'
                      }}
                    />
                  </div>
                  <button
                    className="w-full font-semibold rounded transition-all"
                    style={{ 
                      padding: 'calc(var(--preview-spacing-padding) / 1.5)',
                      background: 'var(--preview-primary-bg)',
                      color: 'var(--preview-primary-text)',
                      borderRadius: 'calc(var(--preview-border-radius) * 0.8)',
                      fontSize: 'var(--preview-font-size-base)',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    Create Customer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

